/**
 * Helper functions and components for SpeakingInterface
 */

import type { TFunction } from 'i18next';

export type SpeakingMode = 'mic-setup' | 'section-audio' | 'context-audio' | 'prep' | 'recording' | 'processing' | 'done' | 'live-conversation';

export function getModeColor(mode: SpeakingMode): string {
    switch (mode) {
        case 'mic-setup':
            return 'border-border/60';
        case 'section-audio':
        case 'context-audio':
        case 'prep':
            return 'border-exam-prep';
        case 'recording':
            return 'border-exam-recording';
        case 'processing':
            return 'border-exam-processing';
        case 'done':
            return 'border-exam-success';
        case 'live-conversation':
            // Live conversation renders its own full interface; this is a safe default.
            return 'border-exam-recording';
    }
}

export function getModeLabel(mode: SpeakingMode, t: TFunction): string {
    switch (mode) {
        case 'mic-setup':
            return t('simulation.speaking.micSetup.heading', { defaultValue: "Let's check your microphone" });
        case 'context-audio':
            return t('simulation.speaking.modes.contextAudio', { defaultValue: 'Playing Context Audio' });
        case 'section-audio':
            return t('simulation.speaking.modes.sectionAudio', { defaultValue: 'Section Intro Audio' });
        case 'prep':
            return t('simulation.speaking.modes.prep', { defaultValue: 'Preparation Time' });
        case 'recording':
            return t('simulation.speaking.modes.recording', { defaultValue: 'Recording Live' });
        case 'processing':
            return t('common.processing', { defaultValue: 'Processing...' });
        case 'done':
            return t('simulation.recording.completed', { defaultValue: 'Recording Complete' });
        case 'live-conversation':
            return t('simulation.conversation.modes.active', { defaultValue: 'Live Conversation' });
    }
}

export function getSkipButtonText(mode: SpeakingMode, t: TFunction): string {
    if (mode === 'section-audio') {
        return t('simulation.speaking.skipAudioContinue', { defaultValue: 'Skip Audio & Continue' });
    }
    if (mode === 'context-audio') {
        return t('simulation.speaking.skipAudioRecord', { defaultValue: 'Skip Audio & Record' });
    }
    return t('simulation.speaking.startEarly', { defaultValue: 'Start Recording Early' });
}

export function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
}

export function getModeBadgeClassName(mode: SpeakingMode): string {
    const baseClasses = "absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] sm:text-sm font-semibold font-ui whitespace-nowrap";

    if (mode === 'mic-setup') {
        return `${baseClasses} bg-muted text-muted-foreground`;
    }
    if (mode === 'prep' || mode === 'context-audio' || mode === 'section-audio') {
        return `${baseClasses} bg-exam-prep text-white`;
    }
    if (mode === 'recording') {
        return `${baseClasses} bg-exam-recording text-white animate-pulse`;
    }
    if (mode === 'processing') {
        return `${baseClasses} bg-exam-processing text-white`;
    }
    if (mode === 'done') {
        return `${baseClasses} bg-exam-success text-white`;
    }

    return baseClasses;
}
